'use client'

import React from 'react'
import { Button, Flex, Text } from '@mantine/core'
import { useRouter } from 'next/navigation'
import { MessagesTextCircleIcon } from '@/components/Icons/MessagesTextCircleIcon'
import { useContactSupport } from '@/hooks/useContactSupport'
import { useUserContext } from '@/contexts/UserContext'
import { StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'
import { IconCircleButton } from './IconCircleButton'
import { NavTheme } from './theme'

interface TextUsButtonProps {
    theme: NavTheme
    iconOnly?: boolean
}

export function TextUsButton({ theme, iconOnly = false }: TextUsButtonProps) {
    const { user } = useUserContext()
    const router = useRouter()
    const { contactUsLabel, contactUsAction } = useContactSupport({
        defaultLabel: 'Text our care team',
        defaultAction: (openSms) => (user ? openSms() : router.push('/get-started')),
        onAction: (destination) =>
            trackStoreEvent(StoreAnalytics.NAV_HEADER_CLICKED, {
                name: 'text-us',
                destination: destination === 'message-center' ? 'message-center' : user ? 'sms' : 'get-started',
            }),
    })

    if (iconOnly) {
        return (
            <IconCircleButton
                theme={theme}
                onClick={contactUsAction}
                data-testid="text-support-icon-button"
                aria-label={contactUsLabel}
            >
                <MessagesTextCircleIcon color={theme.iconColor} />
            </IconCircleButton>
        )
    }

    return (
        <Button
            variant={theme.pillVariant}
            bd={theme.border}
            h={44}
            data-testid="text-support-button"
            onClick={contactUsAction}
        >
            <Flex justify="center" align="center" gap={8}>
                <MessagesTextCircleIcon color={theme.iconColor} />
                <Text fz="md" fw={550} lh="20px" c={theme.textColor}>
                    {contactUsLabel}
                </Text>
            </Flex>
        </Button>
    )
}
